import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Two icons sharing one slot, cross-fading on a loop.
 *
 * Used by the two About technology cards that name two things each: React and Next.js,
 * and Mobile Development, which covers both the App Store and Android. Showing one icon
 * on a card that names two was the original problem; showing both, in turn, states the
 * pair honestly in the space of one icon.
 *
 * WHY THIS IS NOT ServicesHeroIcons
 *
 * That component (DMX-027) also swaps two icons, and its header notes it was
 * deliberately not generalised because it had a single caller. This is that second
 * caller, so the question was live rather than theoretical. It is still the wrong thing
 * to reuse:
 *
 *   - The hero has TWO slots side by side and the icons trade places between them. The
 *     payoff is positional, you notice they have traded sides. Here there is ONE slot,
 *     so there are no places to trade and the entire effect would be lost.
 *   - Its sequence is a theatrical glitch-corrupt-fade-exchange built to carry a hero.
 *     Inside a 48px icon in a grid of eight, that reads as a fault, not a flourish.
 *
 * Generalising it would have meant a props API for slot count, animation set and
 * timings, where two of the three callers use different geometry. That is the
 * speculative API its own comment warned against. So: separate component, same house
 * style, and the timings are exported the way SERVICES_HERO_TIMINGS is so tests assert
 * against these numbers rather than restating them.
 *
 * The exact treatment is a placeholder pending sign-off, hence a plain cross-fade.
 */
export const ALTERNATING_ICON_TIMINGS = {
  /** How long each icon holds before handing over. */
  HOLD_MS: 3200,
  /** The cross-fade itself. Drives the inline transition below, so the two cannot drift. */
  FADE_MS: 500,
} as const

interface AlternatingIconsProps {
  first: ReactNode
  second: ReactNode
  className?: string
}

const AlternatingIcons = ({ first, second, className = 'w-14 h-14' }: AlternatingIconsProps) => {
  const [showFirst, setShowFirst] = useState(true)
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined)

  useEffect(() => {
    // Someone who has asked for less motion gets the first icon, held still. The card
    // heading already names both technologies, so nothing is lost but the movement.
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return

    timer.current = setInterval(() => setShowFirst((v) => !v), ALTERNATING_ICON_TIMINGS.HOLD_MS)
    return () => clearInterval(timer.current)
  }, [])

  return (
    // Decorative. The heading beside it names both technologies, so announcing the
    // icons would only repeat it, and a slot whose contents change on a timer is
    // exactly what a live region should not be.
    <div
      className={`relative ${className}`}
      aria-hidden="true"
      style={{ aspectRatio: '1 / 1', contain: 'layout' }}
    >
      {/*
        The duration comes from FADE_MS rather than a duration-500 class. When the two
        were declared separately the constant was exported, asserted in a test, and
        driving nothing, so a change to either could silently leave them disagreeing.
      */}
      <div
        className={`absolute inset-0 transition-opacity ${showFirst ? 'opacity-100' : 'opacity-0'}`}
        style={{ transitionDuration: `${ALTERNATING_ICON_TIMINGS.FADE_MS}ms` }}
      >
        {first}
      </div>
      <div
        className={`absolute inset-0 transition-opacity ${showFirst ? 'opacity-0' : 'opacity-100'}`}
        style={{ transitionDuration: `${ALTERNATING_ICON_TIMINGS.FADE_MS}ms` }}
      >
        {second}
      </div>
    </div>
  )
}

export default AlternatingIcons
